import styles from "./main.module.scss";
import { ReactComponent as Logo } from "../../assets/James-Hope-University-Logo.svg";
import Stepper from "../../components/stepper/stepper";
import {
  Institution,
  PersonalInfo,
  Qualification,
  RequiredDocument,
  Review,
  UtmeResult,
  WorkHistory,
} from "../../components/onboardingSteps";
import { useAtom, useAtomValue } from "jotai";
import { pageIndexAtom, userAtom } from "../../utils/store";
import { useQuery } from "@tanstack/react-query";
import { getAllDisplayTab } from "../../utils/request";
import { logout } from "../../utils/logout";
import { useApplicationPeriod } from "../../hooks/useApplicationPeriod";
import ProgramInfo from "../../components/programInfo";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../routes";

type TabKey =
  | "personal-info"
  | "qualification"
  | "required-documents"
  | "utme"
  | "work-history"
  | "institution"
  | "review";

const normalizeTabName = (tabName: string) =>
  tabName.trim().toLowerCase().replace(/\s+/g, " ");

const getTabKey = (tabName: string): TabKey | null => {
  const normalizedTabName = normalizeTabName(tabName);

  if (
    normalizedTabName === "personal information" ||
    normalizedTabName === "bio-data" ||
    normalizedTabName === "bio data" ||
    normalizedTabName === "biodata"
  ) {
    return "personal-info";
  }

  if (
    normalizedTabName === "qualification" ||
    normalizedTabName === "qualifications"
  ) {
    return "qualification";
  }

  if (
    normalizedTabName === "required document" ||
    normalizedTabName === "required documents"
  ) {
    return "required-documents";
  }

  if (
    normalizedTabName === "utme" ||
    normalizedTabName === "utme score" ||
    normalizedTabName === "utme result" ||
    normalizedTabName === "utme results"
  ) {
    return "utme";
  }

  if (
    normalizedTabName === "work experience" ||
    normalizedTabName === "work history"
  ) {
    return "work-history";
  }

  if (
    normalizedTabName === "institution" ||
    normalizedTabName === "institutions"
  ) {
    return "institution";
  }

  if (normalizedTabName === "review") {
    return "review";
  }

  return null;
};

const isBachelorApplicant = (programType?: string) =>
  programType?.trim().toLowerCase().startsWith("b") ?? false;

const getNormalizedModeOfEntry = (modeOfEntry?: string) =>
  modeOfEntry?.trim().toLowerCase().replace(/\s+/g, "");

const shouldIncludeTab = (
  tab: DisplayTab,
  programType?: string,
  modeOfEntry?: string,
) => {
  if (!isBachelorApplicant(programType)) return true;

  const tabKey = getTabKey(tab.tabName);
  const normalizedModeOfEntry = getNormalizedModeOfEntry(modeOfEntry);

  if (normalizedModeOfEntry?.includes("directentry")) {
    if (tabKey === "utme") return false;
    if (tabKey === "institution") return true;
  }

  if (normalizedModeOfEntry?.includes("utme")) {
    if (tabKey === "institution") return false;
    if (tabKey === "utme") return true;
  }

  return true;
};

const hasSavedUtmeResult = () => {
  try {
    const savedUtmeResult = JSON.parse(
      localStorage.getItem("utme-result") ?? "null",
    );

    return !!(
      savedUtmeResult?.examNumber &&
      savedUtmeResult?.examYear &&
      savedUtmeResult?.jambResultMeta &&
      savedUtmeResult?.subjects?.length
    );
  } catch (_error) {
    return false;
  }
};

const hasCompletedRequiredDocumentsStep = () =>
  localStorage.getItem("required-documents-completed") === "true";

const tabComponents: Record<TabKey, JSX.Element> = {
  "personal-info": <PersonalInfo onClose={() => {}} />,
  qualification: <Qualification />,
  "required-documents": <RequiredDocument />,
  utme: <UtmeResult />,
  "work-history": <WorkHistory />,
  institution: <Institution />,
  review: <Review />,
};

const ONBOARDING_FORCE_START_KEY = "force-onboarding-start";

const Onboarding = () => {
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const user = useAtomValue(userAtom);
  const { isApplicationPeriodActive } = useApplicationPeriod();
  const navigate = useNavigate();
  const shouldForceFirstStepRef = useRef(
    sessionStorage.getItem(ONBOARDING_FORCE_START_KEY) === "true",
  );

  const tabParams = {
    sessionId: user?.sessionId ?? 0,
    batchName: user?.applicationName ?? "",
    programType: user?.programTypeId ?? 0,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["get-display-tab"],
    queryFn: () => getAllDisplayTab(tabParams),
  });

  const sortedTabs = useMemo(
    () =>
      data?.data
        ?.slice()
        .sort((a, b) => a.tabNumber - b.tabNumber)
        .filter((tab) =>
          shouldIncludeTab(tab, user?.programType, user?.modeOfEntry),
        ) ?? [],
    [data?.data, user?.modeOfEntry, user?.programType],
  );

  useEffect(() => {
    if (user?.isPaymentSubmitted) {
      navigate(routes.dashboard.dashboard);
    }
  }, [navigate, user?.isPaymentSubmitted]);

  const findFirstIncompleteTab = useCallback(() => {
    if (!sortedTabs.length) return 1;

    for (let index = 0; index < sortedTabs.length; index += 1) {
      const tab = sortedTabs[index];
      const tabKey = getTabKey(tab.tabName);
      const stepPosition = index + 1;

      if (tabKey === "personal-info" && user?.isPersonalInfoSubmitted !== true)
        return stepPosition;

      if (tabKey === "qualification" && user?.isqualificationSubmitted !== true)
        return stepPosition;

      if (
        tabKey === "required-documents" &&
        !hasCompletedRequiredDocumentsStep()
      ) {
        return stepPosition;
      }

      if (tabKey === "utme" && !hasSavedUtmeResult()) {
        return stepPosition;
      }

      if (tabKey === "work-history" && user?.isworkhistorySubmitted !== true) {
        return stepPosition;
      }

      if (tabKey === "institution" && user?.iInstitutionSubmitted !== true) {
        return stepPosition;
      }
    }

    return sortedTabs.length;
  }, [sortedTabs, user]);

  useEffect(() => {
    if (!sortedTabs.length) return;

    if (shouldForceFirstStepRef.current) {
      shouldForceFirstStepRef.current = false;
      sessionStorage.removeItem(ONBOARDING_FORCE_START_KEY);
      setPageIndex(1);
      return;
    }

    const firstIncompleteTab = findFirstIncompleteTab();
    setPageIndex((currentPageIndex) =>
      currentPageIndex !== firstIncompleteTab
        ? firstIncompleteTab
        : currentPageIndex,
    );
  }, [findFirstIncompleteTab, setPageIndex, sortedTabs]);

  const steps = () => {
    if (isLoading) return <p>Loading...</p>;
    if (isError) return <p>Error: {error.message}</p>;

    const currentTab = sortedTabs[pageIndex - 1];
    const currentTabKey = currentTab ? getTabKey(currentTab.tabName) : null;

    if (currentTabKey) {
      return tabComponents[currentTabKey];
    }

    return <Review />;
  };

  const displaySteps = sortedTabs.map((item) => item?.tabName);

  const isActiveApplicationPeriod = isApplicationPeriodActive();

  if (!isActiveApplicationPeriod) {
    return (
      <main className={styles.onboarding}>
        <nav className={styles.nav}>
          <Logo />
        </nav>
        <button onClick={logout} className={styles.logout}>
          Logout
        </button>
        <hr className={styles.divider} />
        <br />

        <div className={styles.card}>
          <h2>Application Period Inactive</h2>
          <p>
            The application period is currently inactive. Please check back
            later.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.onboarding}>
      <nav className={styles.nav}>
        <Logo />
      </nav>
      <button onClick={logout} className={styles.logout}>
        Logout
      </button>
      <hr className={styles.divider} />
      <br />
      <ProgramInfo className={styles.programInfo} />
      <br />
      <Stepper
        titles={displaySteps}
        noOfSteps={displaySteps?.length}
        currentStep={Number(pageIndex)}
      />
      <br />

      <div className={styles.card}>
        {steps()}
        <br />
      </div>
    </main>
  );
};

export default Onboarding;
